import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Clock, CreditCard, Car, Shield } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { businessInfo } from '../mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Terms = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    agreed: false
  });
  
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitStatus(null);

    try {
      const submissionData = {
        ...formData,
        acceptanceDate: new Date().toISOString(),
        userAgent: navigator.userAgent
      };

      const response = await axios.post(`${API}/terms-acceptance`, submissionData);
      
      if (response.data.success) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          agreed: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting terms acceptance:', error);
      setSubmitStatus('error');
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link 
              to="/" 
              className="inline-flex items-center text-red-600 hover:text-red-700 mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Link>
            
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Terms & Conditions
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Please read these terms and conditions carefully before booking lessons with Drive Inverclyde Instructor.
            </p>
            
            <div className="mt-8 p-4 bg-red-50 border-l-4 border-red-600 rounded-lg">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-6 h-6 text-red-600 mt-1" />
                <div>
                  <p className="text-red-800 font-medium">Important Notice</p>
                  <p className="text-red-700 text-sm mt-1">
                    BY BOOKING ANY LESSONS WITH DRIVE INVERCLYDE YOU AGREE TO THE BELOW TERMS AND CONDITIONS.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-8">
            
            {/* Lesson Cancellation */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-3">
                  <Clock className="w-6 h-6 text-red-600" />
                  <span>Lesson Cancellation Policy</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  A minimum of <strong>24 hours' notice</strong> is required to be given to the instructor by the pupil to cancel a lesson. 
                  Should the pupil fail to give the instructor sufficient notice, the pupil is liable to pay the full driving lesson fee (£40).
                </p>
              </CardContent>
            </Card>

            {/* CCTV Privacy Notice */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-3">
                  <Shield className="w-6 h-6 text-red-600" />
                  <span>Privacy Notice - Use of CCTV</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded-lg">
                  <p className="text-blue-800 font-medium mb-2">ICO Compliance Notice</p>
                  <p className="text-blue-700 text-sm">
                    <strong>Instructor:</strong> Paul Spencer<br/>
                    <strong>Business:</strong> Drive Inverclyde<br/>
                    <strong>Contact:</strong> <a href="mailto:paul@driveinverclyde.co.uk" className="text-blue-600 hover:text-blue-800 underline">paul@driveinverclyde.co.uk</a>
                  </p>
                </div>
                
                <p className="text-gray-700 leading-relaxed">
                  To help ensure safety, training quality, and protection for both instructor and learner, this vehicle is fitted with 
                  <strong> internal and external CCTV cameras</strong> that record video (and may record audio).
                </p>
                
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Recordings are used for:</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start space-x-2">
                      <span className="text-red-600 font-bold">•</span>
                      <span>Monitoring driving lessons for safety and training purposes</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-red-600 font-bold">•</span>
                      <span>Providing evidence in the event of an incident or complaint</span>
                    </li>
                  </ul>
                </div>
                
                <p className="text-gray-700 leading-relaxed">
                  All recordings are stored securely and retained for a limited period (normally up to <strong>30 days</strong>) 
                  unless required for investigation or legal reasons.
                </p>
                
                <p className="text-gray-700 leading-relaxed">
                  Footage is not shared with third parties except where legally necessary (e.g., law enforcement or insurance providers).
                </p>
                
                <div className="bg-green-50 border-l-4 border-green-600 p-4 rounded-lg">
                  <p className="text-green-800 font-medium mb-2">Data Protection Compliance</p>
                  <p className="text-green-700 text-sm">
                    Paul is registered with the Information Commissioner's Office (ICO) as a data controller in accordance with 
                    the UK GDPR and the Data Protection Act 2018.
                  </p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700 font-medium mb-2">Questions about data usage?</p>
                  <div className="text-gray-700 text-sm">
                    <p>📧 <a href="mailto:paul@driveinverclyde.co.uk" className="text-blue-600 hover:text-blue-800 underline">paul@driveinverclyde.co.uk</a></p>
                    <p>📞 <a href="tel:07711887858" className="text-blue-600 hover:text-blue-800 underline">07711 887 858</a></p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pricing */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-3">
                  <CreditCard className="w-6 h-6 text-red-600" />
                  <span>Pricing & Payment</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  Drive Inverclyde reserves the right to change the hourly lesson rate at any time for new bookings and will ensure 
                  he gives reasonable prior notice to the pupil.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>Payment methods:</strong> Cash or bank transfer accepted.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>Refunds:</strong> Refunds are at the discretion of the instructor and can take up to 30 days back to 
                  the initial payment method.
                </p>
              </CardContent>
            </Card>

 
            {/* Test Booking Management */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-3">
                  <Car className="w-6 h-6 text-red-600" />
                  <span>Test Booking Management</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Important Notice */}
                <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-lg">
                  <p className="text-amber-800 font-medium mb-2">Important Notice - DVSA Rule Changes</p>
                  <p className="text-amber-700 text-sm">
                    From 12 May 2026, only the learner driver is permitted to manage their test booking. This means Drive Inverclyde 
                    instructors cannot handle bookings, and the use of cancellation apps, bots, or third-party booking services is 
                    not permitted. By continuing lessons, you agree to manage your booking yourself and comply with all DVSA requirements.
                  </p>
                </div>

                {/* Section 1: Booking You Driving Test */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">1. Booking You Driving Test</h4>
                  <div className="space-y-2 text-gray-700 text-sm pl-4">
                    <p><strong>1.1.</strong> You agree to only book your driving test when your Drive Inverclyde instructor has confirmed that you are ready. You should always consult your instructor before choosing a test date and, where applicable, include your instructor’s reference number. Booking a test without guidance may result in insufficient preparation, a higher chance of failure, or losing your booking due to limited changes..</p>
                    <p><strong>1.2.</strong> Test bookings will be made based on the Learner's availability, local test-centre availability, and the Instructor's professional assessment of the Learner's readiness.</p>
                    <p><strong>1.3.</strong> You must check with the Instructor before <strong>BOOKING ANY TEST</strong> to ensure: (1) the Instructor considers that you are test ready, and (2) the Instructor has availability.</p>
                    <p><strong>1.4.</strong> Once agreed you are close to test standard, the student can look to book a test within an agreed timeframe. If the student books, then the instructor needs to be informed of Date, Time and DTC (Driving Test Centre).</p>
                    <p><strong>1.5.</strong> Pre booked driving tests <strong>MUST NOT</strong> be changed without consulting the instructor, failure to do so can lead to the instructor/car not being available.</p>
                    <p><strong>1.6.</strong> Payment for taking a practical driving test is paid directly to the DVSA, however this does not include the use of the tuition vehicle. Therefore, a payment must be made to your instructor directly for the use of the vehicle and time spent travelling to and from the test center, this will include 1hr lesson beforehand and use of vehicle <strong>(£80)</strong>.</p>
                    <p><strong>1.7.</strong> If the instructor feels that you are not ready as your test date approaches, then a discussion will take place between student and instructor and we will make sure you don't lose your fee by searching for an alternative date within the 10-day DVSA cancellation period.</p>
                  </div>
                </div>

                {/* Section 2: Monitoring Learner Progress */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">2. Monitoring Learner Progress</h4>
                  <div className="space-y-2 text-gray-700 text-sm pl-4">
                    <p><strong>2.1.</strong> After the initial test booking, the Instructor will monitor the Learner's driving progress through scheduled lessons.</p>
                    <p><strong>2.2.</strong> The Learner agrees to attend lessons regularly so that progress can be assessed fairly and accurately.</p>
                    <p><strong>2.3.</strong> The Instructor will provide honest and constructive feedback to help the Learner prepare for the test, Reflective Logs will also be complete after each lesson and emailed to the student assuming they have supplied an email address.</p>
                  </div>
                </div>

                {/* Section 3: Test Readiness Assessment */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">3. Test Readiness Assessment</h4>
                  <div className="space-y-2 text-gray-700 text-sm pl-4">
                    <p><strong>3.1.</strong> The Instructor has a professional duty to ensure that the Learner is adequately prepared for the driving test.</p>
                    <p><strong>3.2.</strong> As the test date approaches, the Instructor will discuss the Learner's progress and readiness.</p>
                    <p><strong>3.3.</strong> If, in the Instructor's professional opinion, the Learner is not at test-ready standard, the Instructor may recommend postponing or moving the test date.</p>
                  </div>
                </div>

                {/* Section 4: Moving or Postponing the Test */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">4. Moving or Postponing the Test</h4>
                  <div className="space-y-2 text-gray-700 text-sm pl-4">
                    <p><strong>4.1.</strong> If the Instructor advises that the test should be postponed, the Learner agrees to follow the Instructor's recommendation in order to prioritise safety and maximise test success.</p>
                    <p><strong>4.2.</strong> The Instructor will assist in rescheduling the test to an appropriate date.</p>
                    <p><strong>4.3.</strong> The Instructor is not liable for any fees, administrative charges, or loss of test fees due to DVSA rules on cancellations or changes.</p>
                    <p><strong>4.4.</strong> If the Learner refuses to move the test despite the Instructor's advice, the Instructor reserves the right to:</p>
                    <div className="ml-4 space-y-1">
                      <p>• withdraw the use of the Instructor's vehicle for the test, and/or</p>
                      <p>• decline to accompany the Learner on the test date.</p>
                    </div>
                  </div>
                </div>

                {/* Section 5: Use of the Instructor's Vehicle for the Test */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">5. Use of the Instructor's Vehicle for the Test</h4>
                  <div className="space-y-2 text-gray-700 text-sm pl-4">
                    <p><strong>5.1.</strong> The Instructor's vehicle may only be used for driving tests if the Instructor is satisfied that the Learner meets the necessary safety and competency standards.</p>
                    <p><strong>5.2.</strong> The Instructor may refuse use of the vehicle if the Learner has not demonstrated sufficient control, hazard awareness, or consistency.</p>
                    <p><strong>5.3.</strong> Lesson attendance, punctuality, and adherence to instructions may also be considered when determining test readiness.</p>
                  </div>
                </div>

                {/* Section 6: Responsibility of the Learner */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">6. Responsibility of the Learner</h4>
                  <div className="space-y-2 text-gray-700 text-sm pl-4">
                    <p><strong>6.1.</strong> The Learner must ensure all information provided for test booking is accurate.</p>
                    <p><strong>6.2.</strong> The Learner must comply with DVSA rules, including bringing the correct documentation to the test.</p>
                    <p><strong>6.3.</strong> The Learner must notify the Instructor immediately of any changes to their health, eyesight, or circumstances that may affect driving.</p>
                  </div>
                </div>

                {/* Section 7: Instructor Liability */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">7. Instructor Liability</h4>
                  <div className="space-y-2 text-gray-700 text-sm pl-4">
                    <p><strong>7.1.</strong> The Instructor will provide professional guidance but cannot guarantee a test pass.</p>
                    <p><strong>7.2.</strong> The Instructor is not responsible for test cancellations by the DVSA, examiner availability, or adverse weather conditions.</p>
                    <p><strong>7.3.</strong> The Instructor's liability is limited to the cost of any lesson or service paid for and not delivered.</p>
                  </div>
                </div>

                {/* Section 8: Payments & Cancellations */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">8. Payments & Cancellations</h4>
                  <div className="space-y-2 text-gray-700 text-sm pl-4">
                    <p><strong>8.1.</strong> Lesson fees must be paid according to the Instructor's standard payment policy.</p>
                    <p><strong>8.2.</strong> Cancellations must be made in accordance with the Instructor's cancellation policy; late cancellations may be chargeable.</p>
                    <p><strong>8.3.</strong> Test-related fees (DVSA or otherwise) are separate from lesson fees.</p>
                  </div>
                </div>

                {/* Section 9: Agreement */}
                <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded-lg">
                  <h4 className="font-semibold text-red-800 mb-3">9. Agreement</h4>
                  <p className="text-red-700 text-sm">
                    By commencing driving lessons, the Learner confirms that they have read, understood, and agree to be bound by these Terms and Conditions.

All practical driving test bookings, amendments and cancellations are the sole responsibility of the Learner. The Learner must consult with the Instructor and agree on a suitable date and time before booking or changing a practical driving test, to ensure the Instructor and tuition vehicle are available, Failure to do so may result in the vehicle or instructor not being available.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Lesson Logistics */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-3">
                  <Clock className="w-6 h-6 text-red-600" />
                  <span>Lesson Logistics</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  The instructor shall arrive 5-10 mins before your lesson and you will be notified by his arrival through the app 
                  and will wait a max of 15 mins after start time.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  The lesson duration is pre-agreed between the instructor and pupil - typically 60 minutes, but may vary accordingly. 
                  If for any reason outside the instructor's control, it is not possible to deliver the full lesson duration, any remaining 
                  time will be added to a later lesson.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  In the event of a mechanical breakdown or other difficulty outside the control of the instructor, the instructor reserves 
                  the right to rearrange lessons at short notice.
                </p>
              </CardContent>
            </Card>

            {/* Legal Requirements */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-3">
                  <Shield className="w-6 h-6 text-red-600" />
                  <span>Legal Requirements & Liability</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  The pupil will be liable for any endorsements and fines which come as a result of breaking speed limits, 
                  or any other infringement of road law.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  The pupil certifies that a suitable valid driving license is held to drive the tuition vehicle. Pupils must inform 
                  their instructor immediately if they receive any endorsements during the period they are receiving tuition.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>Eyesight Requirements:</strong> You must be able to pass the DVSA eyesight check (20m) before driving. 
                  Your instructor reserves the right to cancel tuition should you fail this test.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  The instructor is at liberty to refuse to proceed with a lesson if he has reason to believe that the pupil is in any way 
                  under the influence of drink or drugs at the time of a driving lesson then the lesson will be cancelled <strong>without refund!</strong>
                </p>
              </CardContent>
            </Card>

            {/* Disclosure of Additional Learning Needs */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-3">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                  <span>Disclosure of Additional Learning Needs and Medical Conditions</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded-lg">
                  <p className="text-blue-800 font-medium mb-2">Equality Act 2010 Compliance</p>
                  <p className="text-blue-700 text-sm">
                    Drive Inverclyde is committed to providing inclusive driving tuition in accordance with the Equality Act 2010. 
                    To ensure we can make appropriate reasonable adjustments and provide the best possible learning experience, 
                    we ask that you disclose any relevant conditions.
                  </p>
                </div>

                <p className="text-gray-700 leading-relaxed">
                  Before commencing lessons, you are encouraged to inform your instructor of any <strong>additional learning needs</strong>, 
                  <strong> disabilities</strong>, or <strong>medical conditions</strong> that may affect your ability to learn to drive safely.
                </p>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">This may include, but is not limited to:</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start space-x-2">
                      <span className="text-red-600 font-bold">•</span>
                      <span>Dyslexia, dyspraxia, ADHD, or other specific learning difficulties</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-red-600 font-bold">•</span>
                      <span>Autism spectrum conditions</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-red-600 font-bold">•</span>
                      <span>Physical disabilities or mobility impairments</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-red-600 font-bold">•</span>
                      <span>Visual or hearing impairments</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-red-600 font-bold">•</span>
                      <span>Epilepsy, diabetes, or other medical conditions that may affect driving</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-red-600 font-bold">•</span>
                      <span>Mental health conditions such as anxiety or depression</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-red-600 font-bold">•</span>
                      <span>Any medication that may affect concentration or reaction times</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Why We Ask for This Information:</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start space-x-2">
                      <span className="text-red-600 font-bold">•</span>
                      <span>To tailor our teaching methods and communication style to your individual needs</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-red-600 font-bold">•</span>
                      <span>To make reasonable adjustments to lessons where appropriate</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-red-600 font-bold">•</span>
                      <span>To ensure your safety and the safety of other road users</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-red-600 font-bold">•</span>
                      <span>To advise on any DVLA notification requirements</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-lg">
                  <p className="text-amber-800 font-medium mb-2">Important Notice</p>
                  <p className="text-amber-700 text-sm">
                    Certain medical conditions must be declared to the DVLA. It is <strong>your legal responsibility</strong> to notify 
                    the DVLA of any notifiable conditions. Your instructor can provide guidance, but the responsibility for declaration 
                    lies with you. Failure to disclose relevant conditions may affect your insurance and licence validity.
                  </p>
                </div>

                <div className="bg-green-50 border-l-4 border-green-600 p-4 rounded-lg">
                  <p className="text-green-800 font-medium mb-2">Confidentiality Assurance</p>
                  <p className="text-green-700 text-sm">
                    All information disclosed will be treated in the strictest confidence and used solely for the purpose of 
                    providing appropriate driving tuition. Your data is protected under UK GDPR and the Data Protection Act 2018.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Professional Standards */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-semibold text-gray-900">Professional Standards & Liability</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  <strong>LEGAL LIABILITY:</strong> The pupil should be aware that the instructor's primary objective is to promote road safety 
                  and in doing so may have to take control of the vehicle.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  The instructor will make every effort to instruct the pupil to the highest standard but can in no way be held liable for any 
                  errors that the pupil may make whilst driving and not accompanied by the instructor either before or after a driving test pass.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  The instructor will endeavor to teach the pupil the correct driving skills according to the recommended syllabus issued by 
                  the DVSA within the minimum number of driving lessons required.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Driving tests may only be arranged with the prior agreement of both the instructor and the pupil.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Any complaints are to be dealt with in accordance with the ADI code of practice.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  The instructor will accept no liability for any accidents, injury or damage to any third party caused by the pupil's 
                  recklessness or negligence during a lesson.
                </p>
              </CardContent>
            </Card>

            {/* Additional Terms */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-semibold text-gray-900">Additional Terms</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  With the Learner’s consent, the Instructor may take photographs and/or videos following their practical driving test for use on the Driving School’s website, social media pages and promotional materials. The Instructor may also invite the Learner to leave an honest review of their learning experience. Participation is entirely voluntary, and the Learner is under no obligation to consent to photographs, videos or provide a review.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  The instructor reserves the right to withhold the use of his vehicle for a driving test.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Nothing in these terms and conditions will affect any statutory rights you may have as a consumer.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>Drive Inverclyde reserves the right to change and amend these terms and conditions at any time without prior notice.</strong>
                </p>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <div className="bg-red-50 rounded-lg p-6 border-l-4 border-red-600">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Questions About These Terms?</h3>
              <p className="text-gray-700 mb-4">
                If you have any questions about these terms and conditions, please contact us:
              </p>
              <div className="space-y-2 text-gray-700">
                <p><strong>Phone:</strong> <a href={`tel:${businessInfo.phone.replace(/\s/g, '')}`} className="text-blue-600 hover:text-blue-800 underline">{businessInfo.phone}</a></p>
                <p><strong>Email:</strong> <a href={`mailto:${businessInfo.email}`} className="text-blue-600 hover:text-blue-800 underline">{businessInfo.email}</a></p>
              </div>
            </div>

            {/* Digital Acceptance & PDF Download */}
            <div className="bg-white rounded-lg shadow-xl p-8 border-l-4 border-red-600">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Accept Terms & Conditions</h3>
              
              {/* PDF Download */}
              <div className="mb-8 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-3">📄 Download PDF Copy</h4>
                <p className="text-gray-600 text-sm mb-4">
                  Download a PDF copy of these terms and conditions for your records.
                </p>
                <button 
                  onClick={() => window.print()} 
                  className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
                >
                  Download PDF
                </button>
              </div>

              {/* Digital Acceptance Form */}
              <div className="border-t pt-6">
                <h4 className="font-semibold text-gray-900 mb-4">✍️ Digital Acceptance</h4>
                <p className="text-gray-600 text-sm mb-6">
                  By digitally accepting these terms, you confirm that you have read, understood, and agree to be bound by all terms and conditions.
                  A confirmation email will be sent to both you and our office.
                </p>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Status Messages */}
                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
                      <div className="text-green-600">✅</div>
                      <div>
                        <p className="text-green-800 font-medium">Terms Accepted Successfully!</p>
                        <p className="text-green-700 text-sm">Confirmation emails have been sent to you and our office.</p>
                      </div>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
                      <div className="text-red-600">❌</div>
                      <div>
                        <p className="text-red-800 font-medium">Submission Failed</p>
                        <p className="text-red-700 text-sm">Please try again or contact us directly.</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        placeholder="Enter your full name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        placeholder="Enter your email address"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date of Acceptance
                    </label>
                    <input
                      type="date"
                      value={new Date().toISOString().split('T')[0]}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                    />
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      name="agreed"
                      checked={formData.agreed}
                      onChange={handleChange}
                      required
                      id="terms-acceptance"
                      className="mt-1 w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                    />
                    <label htmlFor="terms-acceptance" className="text-sm text-gray-700">
                      I have read, understood, and agree to be bound by the above Terms & Conditions. 
                      I understand that this constitutes a legally binding electronic signature.
                    </label>
                  </div>
                  
                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={loading || !formData.agreed}
                      className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Processing...
                        </>
                      ) : (
                        '✓ Accept Terms & Conditions'
                      )}
                    </button>
                  </div>
                </form>
                
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-blue-800 text-xs">
                    🔒 <strong>Legal Notice:</strong> Your digital acceptance will be timestamped, recorded with your IP address, 
                    and stored securely for legal compliance. Confirmation emails will be sent to you and admin@driveinverclyde.co.uk.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;